import { api } from './api';
import type {
  FeedItem,
  Comment,
  User,
  SearchResult,
  TrendingTag,
  PaginatedResponse,
} from '@/types';

// ---------------------------------------------------------------------------
// Backend → Frontend response mappers
// ---------------------------------------------------------------------------

/** Map a backend FeedItem (outfit wrapper) to the frontend FeedItem shape. */
function mapFeedItem(raw: Record<string, unknown>): FeedItem {
  const outfit = raw.outfit as Record<string, unknown> | null;
  return {
    id: String(outfit?.id ?? ''),
    type: 'outfit',
    user: {
      id: String(outfit?.user_id ?? ''),
      email: '',
      displayName: String(raw.author_name ?? ''),
      avatarUrl: (raw.author_avatar_url as string) ?? null,
      bio: '',
      provider: 'email',
      isPublic: true,
      createdAt: '',
      updatedAt: '',
      stats: { garmentCount: 0, outfitCount: 0, followersCount: 0, followingCount: 0, sharedOutfitsCount: 0 },
    },
    outfit: outfit
      ? {
          id: String(outfit.id ?? ''),
          userId: String(outfit.user_id ?? ''),
          name: String(outfit.name ?? ''),
          slots: [],
          thumbnailUrl: (outfit.tryon_image_url as string) ?? null,
          tryOnResultUrl: (outfit.tryon_image_url as string) ?? null,
          occasions: outfit.occasion ? [outfit.occasion as string] as never[] : [],
          seasons: [],
          matchScore: null,
          isPublic: Boolean(outfit.is_public),
          isFavorite: false,
          likes: Number(outfit.likes_count ?? 0),
          commentsCount: Number(raw.comments_count ?? 0),
          tags: (outfit.tags as string[]) ?? [],
          createdAt: String(outfit.created_at ?? ''),
          updatedAt: String(outfit.created_at ?? ''),
        }
      : null,
    caption: String((outfit as Record<string, unknown>)?.name ?? ''),
    imageUrl: String((outfit as Record<string, unknown>)?.tryon_image_url ?? ''),
    likes: Number((outfit as Record<string, unknown>)?.likes_count ?? 0),
    commentsCount: Number(raw.comments_count ?? 0),
    isLiked: Boolean(raw.is_liked),
    isSaved: Boolean(raw.is_saved),
    purchaseLinks: [],
    tags: ((outfit as Record<string, unknown>)?.tags as string[]) ?? [],
    createdAt: String((outfit as Record<string, unknown>)?.created_at ?? ''),
  };
}

/** Map backend trending response to frontend TrendingTag array. */
function mapTrendingTags(raw: Record<string, unknown>): TrendingTag[] {
  const tags = raw.tags as Array<Record<string, unknown> | string> | undefined;
  if (!tags) return [];
  return tags.map((t) => {
    if (typeof t === 'string') {
      return { tag: t, count: 0, thumbnailUrl: '' };
    }
    return {
      tag: String(t.tag ?? ''),
      count: Number(t.count ?? 0),
      thumbnailUrl: String(t.thumbnail_url ?? ''),
    };
  });
}

export const socialService = {
  // ── Feed ──────────────────────────────────────────────────────

  async getFeed(cursor?: string, pageSize = 20): Promise<{
    items: FeedItem[];
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    const params: Record<string, unknown> = { page_size: pageSize };
    if (cursor) params.cursor = cursor;
    const { data } = await api.get('/discover/feed', { params });
    return {
      items: ((data.items ?? []) as Record<string, unknown>[]).map(mapFeedItem),
      nextCursor: data.next_cursor ?? null,
      hasMore: Boolean(data.has_next),
    };
  },

  async getFeedItem(id: string): Promise<FeedItem> {
    const { data } = await api.get(`/discover/feed/${id}`);
    return mapFeedItem(data);
  },

  // ── Interactions ──────────────────────────────────────────────

  async like(outfitId: string): Promise<void> {
    await api.post(`/discover/outfits/${outfitId}/like`);
  },

  async unlike(outfitId: string): Promise<void> {
    await api.delete(`/discover/outfits/${outfitId}/like`);
  },

  async comment(outfitId: string, text: string): Promise<Comment> {
    const { data } = await api.post(`/discover/outfits/${outfitId}/comments`, {
      text,
    });
    return {
      id: data.id,
      userId: data.user_id,
      user: {
        id: data.user_id,
        email: '',
        displayName: data.author_name ?? '',
        avatarUrl: data.author_avatar_url ?? null,
        bio: '',
        provider: 'email',
        isPublic: true,
        createdAt: '',
        updatedAt: '',
        stats: { garmentCount: 0, outfitCount: 0, followersCount: 0, followingCount: 0, sharedOutfitsCount: 0 },
      },
      text: data.text,
      likes: 0,
      isLiked: false,
      parentId: null,
      replies: [],
      createdAt: data.created_at,
    };
  },

  async getComments(
    outfitId: string,
    page = 1,
  ): Promise<PaginatedResponse<Comment>> {
    const { data } = await api.get(`/discover/outfits/${outfitId}/comments`, {
      params: { page },
    });
    return data;
  },

  async deleteComment(outfitId: string, commentId: string): Promise<void> {
    await api.delete(`/discover/outfits/${outfitId}/comments/${commentId}`);
  },

  async save(outfitId: string): Promise<void> {
    await api.post(`/discover/outfits/${outfitId}/save`);
  },

  async unsave(outfitId: string): Promise<void> {
    await api.delete(`/discover/outfits/${outfitId}/save`);
  },

  // ── Profiles ──────────────────────────────────────────────────

  async getProfile(userId: string): Promise<User> {
    const { data } = await api.get(`/discover/profiles/${userId}`);
    return {
      id: data.id,
      email: '',
      displayName: data.full_name ?? '',
      avatarUrl: data.avatar_url ?? null,
      bio: '',
      provider: 'email',
      isPublic: true,
      createdAt: '',
      updatedAt: '',
      stats: {
        garmentCount: 0,
        outfitCount: data.outfits_count ?? 0,
        followersCount: data.followers_count ?? 0,
        followingCount: data.following_count ?? 0,
        sharedOutfitsCount: data.outfits_count ?? 0,
      },
    };
  },

  async follow(userId: string): Promise<void> {
    await api.post(`/discover/profiles/${userId}/follow`);
  },

  async unfollow(userId: string): Promise<void> {
    await api.delete(`/discover/profiles/${userId}/follow`);
  },

  async getFollowers(
    userId: string,
    page = 1,
  ): Promise<PaginatedResponse<User>> {
    const { data } = await api.get(`/discover/profiles/${userId}/followers`, {
      params: { page },
    });
    return data;
  },

  async getFollowing(
    userId: string,
    page = 1,
  ): Promise<PaginatedResponse<User>> {
    const { data } = await api.get(`/discover/profiles/${userId}/following`, {
      params: { page },
    });
    return data;
  },

  // ── Search ────────────────────────────────────────────────────

  async search(query: string, type?: string): Promise<SearchResult[]> {
    const { data } = await api.get('/discover/search', {
      params: { q: query, type },
    });
    // Backend returns FeedResponse; map items to SearchResult shape
    const items = (data.items ?? []) as Record<string, unknown>[];
    return items.map((raw) => {
      const outfit = raw.outfit as Record<string, unknown> | null;
      return {
        type: 'outfit' as const,
        id: String(outfit?.id ?? ''),
        title: String(outfit?.name ?? ''),
        subtitle: String(raw.author_name ?? ''),
        thumbnailUrl: String(outfit?.tryon_image_url ?? ''),
        metadata: {},
      };
    });
  },

  // ── Trending ──────────────────────────────────────────────────

  async getTrending(): Promise<TrendingTag[]> {
    const { data } = await api.get('/discover/trending');
    return mapTrendingTags(data);
  },

  // ── Share ─────────────────────────────────────────────────────

  async shareOutfit(
    outfitId: string,
    caption: string,
    tags: string[],
  ): Promise<FeedItem> {
    const { data } = await api.post('/discover/share', {
      outfit_id: outfitId,
      caption,
      tags,
    });
    return mapFeedItem(data);
  },
};
